/* eslint-disable no-return-assign */
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import omit from 'lodash/omit';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import {
  InputFieldValueComponent,
  LookupFieldValueComponent,
  SelectFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import AddIdentificationCodeService from '../../services/identificationcodes/addIdentificationCodeService';
import EditIdentificationCodeService from '../../services/identificationcodes/editIdentificationCodeService';
import IdentificationCodeService from '../../services/identificationcodes/IdentificationCodeService';
import OrganisationCommunitiesService from '../../services/organisationCommunitiesService';
import apiRoutes from '../../../../app/api/routes';
import { ACTIONS, GLOBAL_COMMUNITY_OPTION } from '../../constants';
import { mapIdentificationCodeToForm } from '../../helpers/identificationCodes/mapIdentificationCodeToForm';
import ReadOnlyFieldValueComponent from '../../../app/components/ReadOnlyFieldValueComponent';

const FORM_NAME = 'identificationCode';

const normalizeNoSpaceToStart = value => {
  if (!value) return value;

  // Remove leading spaces
  return value.trimStart();
};

export const validate = (values, { identificationCodeTypesFetch, t }) => {
  const errors = {};
  if (!values.identificationCodeTypeTag) {
    errors.identificationCodeTypeTag = t('pleaseSelectAType');
  }

  if (!values.value || !values.value.trim()) {
    errors.value = t('pleaseEnterAValue');
  }

  // Check for trailing spaces
  if (values.value && values.value !== values.value.trimEnd()) {
    errors.value = t('codeCannotHaveTrailingSpaces');
  }

  const type =
    identificationCodeTypesFetch.value &&
    identificationCodeTypesFetch.value.find(
      ict => String(ict.tag) === values.identificationCodeTypeTag
    );

  if (values.value && type && type.validationPattern) {
    const pattern = new RegExp(type.validationPattern.pattern);
    if (!pattern.test(values.value)) {
      errors.value = type.validationPattern.errorMessage;
    }
  }

  return errors;
};

const IdentificationCodeForm = ({
  organisationId,
  open,
  returnRoute,
  dispatchEditIdentificationCodePut,
  dispatchAddIdentificationCodePost,
  editIdentificationCodeFetch,
  addIdentificationCodeFetch,
  identificationCodeTypesFetch,
  dispatchIdentificationCodeDetailGet,
  identificationCodeDetailFetch,
  dispatchOrganisationCommunitiesGet,
  organisationCommunitiesFetch,
}) => {
  const { t } = useTranslation();
  const { action, actionId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (actionId) dispatchIdentificationCodeDetailGet(actionId, t);
  }, [dispatchIdentificationCodeDetailGet, actionId, t]);

  useEffect(() => dispatchOrganisationCommunitiesGet(organisationId), [
    organisationId,
    dispatchOrganisationCommunitiesGet,
  ]);

  const handleSubmit = useCallback(
    body => {
      const callback = () => navigate(returnRoute);

      let values = body;
      if (body.communityTag === GLOBAL_COMMUNITY_OPTION.value) {
        values = omit(values, 'communityTag');
      }

      if (action === ACTIONS.NEW) {
        dispatchAddIdentificationCodePost({ ...values, organisationId }, callback, t);
      } else {
        dispatchEditIdentificationCodePut(actionId, { ...values, organisationId }, callback, t);
      }
    },
    [
      t,
      actionId,
      organisationId,
      returnRoute,
      action,
      dispatchEditIdentificationCodePut,
      dispatchAddIdentificationCodePost,
      navigate,
    ]
  );

  const defaultData = useMemo(
    () =>
      action !== ACTIONS.NEW && !identificationCodeDetailFetch.pending
        ? mapIdentificationCodeToForm(identificationCodeDetailFetch.value)
        : { communityTag: GLOBAL_COMMUNITY_OPTION.value },
    [action, identificationCodeDetailFetch]
  );

  const communityOptions = useMemo(
    () => [
      GLOBAL_COMMUNITY_OPTION,
      ...(organisationCommunitiesFetch.value || []).map(c => ({
        key: c.tag,
        value: c.tag,
        text: c.name,
      })),
    ],
    [organisationCommunitiesFetch.value]
  );

  if (!open) return null;

  return (
    <FormModal
      t={t}
      identificationCodeTypesFetch={identificationCodeTypesFetch}
      header={action !== ACTIONS.NEW ? t('viewIdentificationCode') : t('addIdentificationCode')}
      dataLoading={false}
      submitProcessing={editIdentificationCodeFetch.pending || addIdentificationCodeFetch.pending}
      onSubmit={handleSubmit}
      data={defaultData}
      validate={validate}
      form={FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      noSaveButton={action !== ACTIONS.NEW}
      saveButtonIcon="save"
      saveButtonContent={action !== ACTIONS.NEW ? t('save') : t('add')}
    >
      <Grid>
        <Grid.Column mobile={16} tablet={8} computer={8}>
          <Field
            fluid
            label={t('code')}
            name="value"
            normalize={normalizeNoSpaceToStart}
            valueComponent={
              action !== ACTIONS.NEW ? ReadOnlyFieldValueComponent : InputFieldValueComponent
            }
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={8} computer={8}>
          <Field
            fluid
            search
            clearable
            label={t('type')}
            name="identificationCodeTypeTag"
            url={apiRoutes.identificationCodeTypes()}
            valueProp="tag"
            valueComponent={
              action !== ACTIONS.NEW ? ReadOnlyFieldValueComponent : LookupFieldValueComponent
            }
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={8} computer={8}>
          <Field
            fluid
            label={t('description')}
            name="description"
            valueComponent={
              action !== ACTIONS.NEW ? ReadOnlyFieldValueComponent : InputFieldValueComponent
            }
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={8} computer={8}>
          <Field
            fluid
            search
            clearable
            label={t('community')}
            name="communityTag"
            options={communityOptions}
            valueComponent={
              action !== ACTIONS.NEW ? ReadOnlyFieldValueComponent : SelectFieldValueComponent
            }
          />
        </Grid.Column>
        {action !== ACTIONS.NEW && (
          <>
            <Grid.Column mobile={16} tablet={8} computer={8}>
              <Field
                fluid
                isDateTime
                label={t('createdOn')}
                name="createdOn"
                valueComponent={
                  action !== ACTIONS.NEW ? ReadOnlyFieldValueComponent : InputFieldValueComponent
                }
              />
            </Grid.Column>
            <Grid.Column mobile={16} tablet={8} computer={8}>
              <Field
                fluid
                isDateTime
                label={t('lastModifiedOn')}
                name="modifiedOn"
                valueComponent={
                  action !== ACTIONS.NEW ? ReadOnlyFieldValueComponent : InputFieldValueComponent
                }
              />
            </Grid.Column>
          </>
        )}
      </Grid>
    </FormModal>
  );
};

IdentificationCodeForm.propTypes = {
  dispatchOrganisationCommunitiesGet: PropTypes.func.isRequired,
  organisationCommunitiesFetch: PropTypes.object.isRequired,
  dispatchEditIdentificationCodePut: PropTypes.func.isRequired,
  dispatchAddIdentificationCodePost: PropTypes.func.isRequired,
  editIdentificationCodeFetch: PropTypes.object.isRequired,
  addIdentificationCodeFetch: PropTypes.object.isRequired,
  identificationCodeTypesFetch: PropTypes.object.isRequired,
  identificationCodeDetailFetch: PropTypes.object.isRequired,
  dispatchIdentificationCodeDetailGet: PropTypes.func.isRequired,
  returnRoute: PropTypes.string.isRequired,
  organisationId: PropTypes.string.isRequired,
  open: PropTypes.bool,
};

IdentificationCodeForm.defaultProps = { open: false };

export default compose(
  lookupService({
    resource: 'identificationCodeTypes',
    url: apiRoutes.identificationCodeTypes(),
  }),
  OrganisationCommunitiesService,
  IdentificationCodeService,
  AddIdentificationCodeService,
  EditIdentificationCodeService
)(IdentificationCodeForm);
